import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { hasAuth, validateAuth, signIn } from 'libraries/amplify.library';
import { Box, CircularProgress, Typography } from '@mui/material';

interface ProtectedRouteProps {
    children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
    const [isChecking, setIsChecking] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const checkAuth = async () => {
            // Quick presence check first
            if (!hasAuth()) {
                const urlParams = new URLSearchParams(window.location.search);
                const code = urlParams.get('code');
                
                if (!code) {
                    console.log('User not authenticated, redirecting to login...');
                    await signIn();
                    return;
                }
                // If we have a code, let the Hub listener handle it
                setIsChecking(false);
                return;
            }
            
            // Validate token authenticity and expiration
            const authStatus = await validateAuth();
            
            if (!authStatus) {
                console.log('Token validation failed, redirecting to login...');
                await signIn();
                return;
            }
            
            setIsAuthenticated(authStatus);
            setIsChecking(false);
        };

        checkAuth();
    }, [location]);

    if (isChecking) {
        return (
            <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                minHeight="100vh"
            >
                <CircularProgress />
                <Typography variant="body1" sx={{ mt: 2 }}>
                    Checking authentication...
                </Typography>
            </Box>
        );
    }

    if (!isAuthenticated) {
        // Show loading while redirect happens
        return (
            <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                minHeight="100vh"
            >
                <CircularProgress />
                <Typography variant="body1" sx={{ mt: 2 }}>
                    Redirecting to login...
                </Typography>
            </Box>
        );
    }

    return <>{children}</>;
};

export default ProtectedRoute;
